/**
 * Helper to generate and download a genuine Microsoft Word (.docx) document
 * replicating the exact layout and visual design of Preview MPR.
 */
import JSZip from "jszip";
import { toast } from "react-toastify";
import { ConvertDateTime } from "../../Base/helper/baseFunctions";

const formatDate = (dateStr) => {
  if (!dateStr) return "";
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    return `${day}-${month}-${year}`;
  } catch {
    return dateStr;
  }
};

const escapeXml = (str) => {
  if (!str) return "";
  return String(str)
    .replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F-\x84\x86-\x9F]/g, "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;");
};

const formatCtc = (val) => {
  if (val === undefined || val === null || val === "") return "-";
  const num = Number(String(val).replace(/[^0-9.-]+/g, ""));
  if (isNaN(num)) return String(val);
  return `₹${num.toLocaleString("en-IN")}`;
};

const stripHtml = (html) => {
  if (!html) return "";
  const tmp = document.createElement("DIV");
  tmp.innerHTML = html;
  return (tmp.textContent || tmp.innerText || "").trim();
};

// 2-column card box (field label + value)
const makeFieldCell = (label, value) => `
  <w:tc>
    <w:tcPr>
      <w:tcW w:w="2500" w:type="pct"/>
      <w:tcBorders>
        <w:top w:val="single" w:sz="6" w:space="0" w:color="CCCCCC"/>
        <w:left w:val="single" w:sz="6" w:space="0" w:color="CCCCCC"/>
        <w:bottom w:val="single" w:sz="6" w:space="0" w:color="CCCCCC"/>
        <w:right w:val="single" w:sz="6" w:space="0" w:color="CCCCCC"/>
      </w:tcBorders>
      <w:shd w:val="clear" w:color="auto" w:fill="FFFFFF"/>
      <w:tcMar>
        <w:top w:w="80" w:type="dxa"/>
        <w:left w:w="140" w:type="dxa"/>
        <w:bottom w:w="80" w:type="dxa"/>
        <w:right w:w="140" w:type="dxa"/>
      </w:tcMar>
    </w:tcPr>
    <w:p>
      <w:pPr>
        <w:spacing w:before="0" w:after="20"/>
        <w:rPr>
          <w:rFonts w:ascii="Arial" w:hAnsi="Arial" w:cs="Arial"/>
          <w:b/>
          <w:color w:val="111827"/>
          <w:sz w:val="25"/>
          <w:szCs w:val="25"/>
        </w:rPr>
      </w:pPr>
      <w:r>
        <w:rPr>
          <w:rFonts w:ascii="Arial" w:hAnsi="Arial" w:cs="Arial"/>
          <w:b/>
          <w:color w:val="111827"/>
          <w:sz w:val="25"/>
          <w:szCs w:val="25"/>
        </w:rPr>
        <w:t xml:space="preserve">${escapeXml(label)}</w:t>
      </w:r>
    </w:p>
    <w:p>
      <w:pPr>
        <w:spacing w:before="0" w:after="0"/>
        <w:rPr>
          <w:rFonts w:ascii="Arial" w:hAnsi="Arial" w:cs="Arial"/>
          <w:color w:val="111827"/>
          <w:sz w:val="25"/>
          <w:szCs w:val="25"/>
        </w:rPr>
      </w:pPr>
      <w:r>
        <w:rPr>
          <w:rFonts w:ascii="Arial" w:hAnsi="Arial" w:cs="Arial"/>
          <w:color w:val="111827"/>
          <w:sz w:val="25"/>
          <w:szCs w:val="25"/>
        </w:rPr>
        <w:t xml:space="preserve">${escapeXml(value || " ")}</w:t>
      </w:r>
    </w:p>
  </w:tc>`;

// Empty invisible cell without borders
const makeEmptyCell = () => `
  <w:tc>
    <w:tcPr>
      <w:tcW w:w="2500" w:type="pct"/>
      <w:tcBorders>
        <w:top w:val="none"/>
        <w:left w:val="none"/>
        <w:bottom w:val="none"/>
        <w:right w:val="none"/>
      </w:tcBorders>
      <w:shd w:val="clear" w:color="auto" w:fill="auto"/>
    </w:tcPr>
    <w:p>
      <w:pPr>
        <w:spacing w:before="0" w:after="0"/>
      </w:pPr>
    </w:p>
  </w:tc>`;

// Large text box (Job Description, Business Requirement, Remarks)
const makeBoxCell = (title, content, widthPct = 2500) => {
  const rawContent = String(content || "").trim();
  const lines = rawContent.length > 0 ? rawContent.split(/\r?\n/) : [" "];
  const paragraphsXml = lines
    .map(
      (line, idx) => `
    <w:p>
      <w:pPr>
        <w:spacing w:before="0" w:after="${idx === lines.length - 1 ? "0" : "40"}"/>
        <w:rPr>
          <w:rFonts w:ascii="Arial" w:hAnsi="Arial" w:cs="Arial"/>
          <w:color w:val="111827"/>
          <w:sz w:val="25"/>
          <w:szCs w:val="25"/>
        </w:rPr>
      </w:pPr>
      <w:r>
        <w:rPr>
          <w:rFonts w:ascii="Arial" w:hAnsi="Arial" w:cs="Arial"/>
          <w:color w:val="111827"/>
          <w:sz w:val="25"/>
          <w:szCs w:val="25"/>
        </w:rPr>
        <w:t xml:space="preserve">${escapeXml(line.trim() || " ")}</w:t>
      </w:r>
    </w:p>`
    )
    .join("");

  return `
  <w:tc>
    <w:tcPr>
      <w:tcW w:w="${widthPct}" w:type="pct"/>
      <w:tcBorders>
        <w:top w:val="single" w:sz="6" w:space="0" w:color="CCCCCC"/>
        <w:left w:val="single" w:sz="6" w:space="0" w:color="CCCCCC"/>
        <w:bottom w:val="single" w:sz="6" w:space="0" w:color="CCCCCC"/>
        <w:right w:val="single" w:sz="6" w:space="0" w:color="CCCCCC"/>
      </w:tcBorders>
      <w:shd w:val="clear" w:color="auto" w:fill="FFFFFF"/>
      <w:tcMar>
        <w:top w:w="80" w:type="dxa"/>
        <w:left w:w="140" w:type="dxa"/>
        <w:bottom w:w="80" w:type="dxa"/>
        <w:right w:w="140" w:type="dxa"/>
      </w:tcMar>
    </w:tcPr>
    <w:p>
      <w:pPr>
        <w:spacing w:before="0" w:after="20"/>
        <w:rPr>
          <w:rFonts w:ascii="Arial" w:hAnsi="Arial" w:cs="Arial"/>
          <w:b/>
          <w:color w:val="111827"/>
          <w:sz w:val="25"/>
          <w:szCs w:val="25"/>
        </w:rPr>
      </w:pPr>
      <w:r>
        <w:rPr>
          <w:rFonts w:ascii="Arial" w:hAnsi="Arial" w:cs="Arial"/>
          <w:b/>
          <w:color w:val="111827"/>
          <w:sz w:val="25"/>
          <w:szCs w:val="25"/>
        </w:rPr>
        <w:t xml:space="preserve">${escapeXml(title)}</w:t>
      </w:r>
    </w:p>
    ${paragraphsXml}
  </w:tc>`;
};

const mprFields = [
  { label: "Hiring Type", key: "hiring_type" },
  { label: "Employment Type", key: "employmenttype_name" },

  { label: "Designation *", key: "role" },
  {label: "Role", key: "sub_role"},
  { label: "Department *", key: "bu_name" },
  { label: "Grade", key: "grade" },

  { label: "Requested Headcount *", key: "headcount" },
  { label: "Approved Headcount", key: "approved_headcount", format: "approvedHeadcount" },

  { label: "Min Salary", key: "min_salary" },
  { label: "Max Salary", key: "max_salary" },

  { label: "Min Experience", key: "min_experience" },
  { label: "Max Experience", key: "max_experience" },

  {
    label: "Budget Status",
    key: "is_budgeted",
    format: "budgetStatus",
  },

  {
    label: "Requested Budget *",
    key: "max_budget",
    format: "requestedBudget",
  },
  {
    label: "Approved Budget",
    key: "approver_budget",
    format: "approvedBudget",
  },

  {
    label: "Target Date for Hiring",
    key: "target_date",
    format: "date",
  },
  { label: "Location", key: "location" },

  { label: "Approver *", key: "approver_name" },
  { label: "Second Approver", key: "second_approver_emp_name" },

  { label: "Third Approver", key: "third_approver_emp_name" },
  { label: "Requested By", key: "mpr_owner_name" },
  { label: "Requested On", key: "createdon", format: "requestedOn" },
  
];
const getFieldValue = (record,field) => {
  const value = record?.[field.key];

  switch (field.format) {
    case "date":
      return formatDate(value) || "-";

    case "requestedOn":
      return ConvertDateTime(value) || "-";

    case "budgetStatus":
      return record?.is_budgeted === false
        ? "Not Budgeted"
        : "Budgeted";

    case "requestedBudget":
      return record?.is_budgeted === false
        ? "Non Budgeted"
        : value !== undefined && value !== null && value !== ""
        ? String(value)
        : "N/A";

    case "approvedBudget":
      if (record?.is_budgeted === false) {
        return "Non Budgeted";
      }

      if (!record?.mpr_status || record.mpr_status === "Rejected") {
        return "-";
      }

      return record?.approved_budget ??
        record?.approver_budget ??
        record?.max_budget ??
        "N/A";
    case "approvedHeadcount":
      if (!record?.mpr_status || record.mpr_status === "Rejected") {
        return "-";
      }
      return record?.approved_headcount || "N/A";
    default:
      return value !== undefined && value !== null && value !== ""
        ? String(value)
        : "-";
  }
};

export const buildMprDocZip = (record) => {
  if (!record) {
    throw new Error("No MPR data available to generate document");
  }

  const mprNo = record?.mpr_no || record?.mpr_requests_pk || "MPR";
  const status = record?.mpr_status || record?.status || "Submitted";
  const hiringType = record?.hiring_type || "New Hire";
  const jdText = stripHtml(record?.jd);
  const businessRequirement = record?.justification_comment || "";
  const additionalRemarks = record?.additional_comments || "";

  let replacementList = [];
  const repData = record?.replacement_details;
  if (repData) {
    if (typeof repData === "string") {
      try {
        replacementList = JSON.parse(repData) || [];
      } catch (e) {
        replacementList = [];
      }
    } else if (Array.isArray(repData)) {
      replacementList = repData;
    }
  }
  const fieldRowsXml = [];

  for (let i = 0; i < mprFields.length; i += 2) {
    const firstField = mprFields[i];
    const secondField = mprFields[i + 1];

    fieldRowsXml.push(`
        <w:tr w:rsidR="00000000">
          <w:trPr>
            <w:tblCellSpacing w:w="80" w:type="dxa"/>
          </w:trPr>

          ${makeFieldCell(
            firstField.label,
            getFieldValue(record,firstField)
          )}

          ${
            secondField
              ? makeFieldCell(
                  secondField.label,
                  getFieldValue(record, secondField)
                )
              : makeEmptyCell()
          }
        </w:tr>
      `);
  }

  // Common Table Properties for perfect 100% full width and clean card-gap alignment
  const tableProps = `
      <w:tblPr>
        <w:tblW w:w="5000" w:type="pct"/>
        <w:tblCellSpacing w:w="80" w:type="dxa"/>
        <w:tblCellMar>
          <w:top w:w="0" w:type="dxa"/>
          <w:left w:w="0" w:type="dxa"/>
          <w:bottom w:w="0" w:type="dxa"/>
          <w:right w:w="0" w:type="dxa"/>
        </w:tblCellMar>
        <w:tblLook w:val="04A0" w:firstRow="1" w:lastRow="0" w:firstColumn="1" w:lastColumn="0" w:noHBand="0" w:noVBand="1"/>
      </w:tblPr>`;

  // Replacement Details Section
  let replacementSectionXml = "";
  if (hiringType === "Replacement" && replacementList.length > 0) {
    const repRowsXml = replacementList
      .map(
        (c, idx) => `
          <w:tr w:rsidR="00000000">
            <w:trPr><w:tblCellSpacing w:w="80" w:type="dxa"/></w:trPr>
            <w:tc>
              <w:tcPr><w:tcW w:w="400" w:type="pct"/><w:tcBorders><w:top w:val="single" w:sz="6" w:space="0" w:color="CCCCCC"/><w:left w:val="single" w:sz="6" w:space="0" w:color="CCCCCC"/><w:bottom w:val="single" w:sz="6" w:space="0" w:color="CCCCCC"/><w:right w:val="single" w:sz="6" w:space="0" w:color="CCCCCC"/></w:tcBorders><w:tcMar><w:top w:w="80" w:type="dxa"/><w:left w:w="120" w:type="dxa"/><w:bottom w:w="80" w:type="dxa"/><w:right w:w="120" w:type="dxa"/></w:tcMar></w:tcPr>
              <w:p><w:pPr><w:jc w:val="center"/><w:spacing w:after="0"/></w:pPr><w:r><w:rPr><w:rFonts w:ascii="Arial" w:hAnsi="Arial"/><w:sz w:val="25"/><w:szCs w:val="25"/><w:color w:val="111827"/></w:rPr><w:t>${idx + 1}</w:t></w:r></w:p>
            </w:tc>
            <w:tc>
              <w:tcPr><w:tcW w:w="1800" w:type="pct"/><w:tcBorders><w:top w:val="single" w:sz="6" w:space="0" w:color="CCCCCC"/><w:left w:val="single" w:sz="6" w:space="0" w:color="CCCCCC"/><w:bottom w:val="single" w:sz="6" w:space="0" w:color="CCCCCC"/><w:right w:val="single" w:sz="6" w:space="0" w:color="CCCCCC"/></w:tcBorders><w:tcMar><w:top w:w="80" w:type="dxa"/><w:left w:w="120" w:type="dxa"/><w:bottom w:w="80" w:type="dxa"/><w:right w:w="120" w:type="dxa"/></w:tcMar></w:tcPr>
              <w:p><w:pPr><w:spacing w:after="0"/></w:pPr><w:r><w:rPr><w:rFonts w:ascii="Arial" w:hAnsi="Arial"/><w:sz w:val="25"/><w:szCs w:val="25"/><w:color w:val="111827"/></w:rPr><w:t xml:space="preserve">${escapeXml(c.name || "-")}</w:t></w:r></w:p>
            </w:tc>
            <w:tc>
              <w:tcPr><w:tcW w:w="1800" w:type="pct"/><w:tcBorders><w:top w:val="single" w:sz="6" w:space="0" w:color="CCCCCC"/><w:left w:val="single" w:sz="6" w:space="0" w:color="CCCCCC"/><w:bottom w:val="single" w:sz="6" w:space="0" w:color="CCCCCC"/><w:right w:val="single" w:sz="6" w:space="0" w:color="CCCCCC"/></w:tcBorders><w:tcMar><w:top w:w="80" w:type="dxa"/><w:left w:w="120" w:type="dxa"/><w:bottom w:w="80" w:type="dxa"/><w:right w:w="120" w:type="dxa"/></w:tcMar></w:tcPr>
              <w:p><w:pPr><w:spacing w:after="0"/></w:pPr><w:r><w:rPr><w:rFonts w:ascii="Arial" w:hAnsi="Arial"/><w:sz w:val="25"/><w:szCs w:val="25"/><w:color w:val="111827"/></w:rPr><w:t xml:space="preserve">${escapeXml(c.designation || "-")}</w:t></w:r></w:p>
            </w:tc>
            <w:tc>
              <w:tcPr><w:tcW w:w="1000" w:type="pct"/><w:tcBorders><w:top w:val="single" w:sz="6" w:space="0" w:color="CCCCCC"/><w:left w:val="single" w:sz="6" w:space="0" w:color="CCCCCC"/><w:bottom w:val="single" w:sz="6" w:space="0" w:color="CCCCCC"/><w:right w:val="single" w:sz="6" w:space="0" w:color="CCCCCC"/></w:tcBorders><w:tcMar><w:top w:w="80" w:type="dxa"/><w:left w:w="120" w:type="dxa"/><w:bottom w:w="80" w:type="dxa"/><w:right w:w="120" w:type="dxa"/></w:tcMar></w:tcPr>
              <w:p><w:pPr><w:spacing w:after="0"/></w:pPr><w:r><w:rPr><w:rFonts w:ascii="Arial" w:hAnsi="Arial"/><w:sz w:val="25"/><w:szCs w:val="25"/><w:color w:val="111827"/></w:rPr><w:t xml:space="preserve">${escapeXml(formatCtc(c.ctc))}</w:t></w:r></w:p>
            </w:tc>
          </w:tr>`
        )
        .join("");

    replacementSectionXml = `
      <!-- Replacement Details Title -->
      <w:p><w:pPr><w:spacing w:before="100" w:after="40"/><w:rPr><w:rFonts w:ascii="Arial" w:hAnsi="Arial"/><w:b/><w:sz w:val="25"/><w:szCs w:val="25"/><w:color w:val="111827"/></w:rPr></w:pPr><w:r><w:rPr><w:rFonts w:ascii="Arial" w:hAnsi="Arial"/><w:b/><w:sz w:val="25"/><w:szCs w:val="25"/><w:color w:val="111827"/></w:rPr><w:t>Replacement Details</w:t></w:r></w:p>
      <w:tbl>
        ${tableProps}
        <w:tblGrid><w:gridCol w:w="836"/><w:gridCol w:w="3762"/><w:gridCol w:w="3762"/><w:gridCol w:w="2090"/></w:tblGrid>
        <w:tr w:rsidR="00000000">
          <w:trPr><w:tblCellSpacing w:w="80" w:type="dxa"/></w:trPr>
          <w:tc><w:tcPr><w:tcW w:w="400" w:type="pct"/><w:shd w:val="clear" w:color="auto" w:fill="F9FAFB"/><w:tcBorders><w:top w:val="single" w:sz="6" w:space="0" w:color="CCCCCC"/><w:left w:val="single" w:sz="6" w:space="0" w:color="CCCCCC"/><w:bottom w:val="single" w:sz="6" w:space="0" w:color="CCCCCC"/><w:right w:val="single" w:sz="6" w:space="0" w:color="CCCCCC"/></w:tcBorders><w:tcMar><w:top w:w="80" w:type="dxa"/><w:left w:w="120" w:type="dxa"/><w:bottom w:w="80" w:type="dxa"/><w:right w:w="120" w:type="dxa"/></w:tcMar></w:tcPr><w:p><w:pPr><w:jc w:val="center"/><w:spacing w:after="0"/></w:pPr><w:r><w:rPr><w:rFonts w:ascii="Arial" w:hAnsi="Arial"/><w:b/><w:sz w:val="25"/><w:szCs w:val="25"/><w:color w:val="111827"/></w:rPr><w:t>S.No</w:t></w:r></w:p></w:tc>
          <w:tc><w:tcPr><w:tcW w:w="1800" w:type="pct"/><w:shd w:val="clear" w:color="auto" w:fill="F9FAFB"/><w:tcBorders><w:top w:val="single" w:sz="6" w:space="0" w:color="CCCCCC"/><w:left w:val="single" w:sz="6" w:space="0" w:color="CCCCCC"/><w:bottom w:val="single" w:sz="6" w:space="0" w:color="CCCCCC"/><w:right w:val="single" w:sz="6" w:space="0" w:color="CCCCCC"/></w:tcBorders><w:tcMar><w:top w:w="80" w:type="dxa"/><w:left w:w="120" w:type="dxa"/><w:bottom w:w="80" w:type="dxa"/><w:right w:w="120" w:type="dxa"/></w:tcMar></w:tcPr><w:p><w:pPr><w:spacing w:after="0"/></w:pPr><w:r><w:rPr><w:rFonts w:ascii="Arial" w:hAnsi="Arial"/><w:b/><w:sz w:val="25"/><w:szCs w:val="25"/><w:color w:val="111827"/></w:rPr><w:t>Name</w:t></w:r></w:p></w:tc>
          <w:tc><w:tcPr><w:tcW w:w="1800" w:type="pct"/><w:shd w:val="clear" w:color="auto" w:fill="F9FAFB"/><w:tcBorders><w:top w:val="single" w:sz="6" w:space="0" w:color="CCCCCC"/><w:left w:val="single" w:sz="6" w:space="0" w:color="CCCCCC"/><w:bottom w:val="single" w:sz="6" w:space="0" w:color="CCCCCC"/><w:right w:val="single" w:sz="6" w:space="0" w:color="CCCCCC"/></w:tcBorders><w:tcMar><w:top w:w="80" w:type="dxa"/><w:left w:w="120" w:type="dxa"/><w:bottom w:w="80" w:type="dxa"/><w:right w:w="120" w:type="dxa"/></w:tcMar></w:tcPr><w:p><w:pPr><w:spacing w:after="0"/></w:pPr><w:r><w:rPr><w:rFonts w:ascii="Arial" w:hAnsi="Arial"/><w:b/><w:sz w:val="25"/><w:szCs w:val="25"/><w:color w:val="111827"/></w:rPr><w:t>Current Designation</w:t></w:r></w:p></w:tc>
          <w:tc><w:tcPr><w:tcW w:w="1000" w:type="pct"/><w:shd w:val="clear" w:color="auto" w:fill="F9FAFB"/><w:tcBorders><w:top w:val="single" w:sz="6" w:space="0" w:color="CCCCCC"/><w:left w:val="single" w:sz="6" w:space="0" w:color="CCCCCC"/><w:bottom w:val="single" w:sz="6" w:space="0" w:color="CCCCCC"/><w:right w:val="single" w:sz="6" w:space="0" w:color="CCCCCC"/></w:tcBorders><w:tcMar><w:top w:w="80" w:type="dxa"/><w:left w:w="120" w:type="dxa"/><w:bottom w:w="80" w:type="dxa"/><w:right w:w="120" w:type="dxa"/></w:tcMar></w:tcPr><w:p><w:pPr><w:spacing w:after="0"/></w:pPr><w:r><w:rPr><w:b/><w:sz w:val="25"/><w:szCs w:val="25"/><w:color w:val="111827"/></w:rPr><w:t>Current CTC</w:t></w:r></w:p></w:tc>
        </w:tr>
        ${repRowsXml}
      </w:tbl>`;
  }

  const documentXml = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<w:document xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships" xmlns:m="http://schemas.openxmlformats.org/officeDocument/2006/math" xmlns:v="urn:schemas-microsoft-com:vml" xmlns:wp="http://schemas.openxmlformats.org/drawingml/2006/wordprocessingDrawing" xmlns:w10="urn:schemas-microsoft-com:office:word" xmlns:w14="http://schemas.microsoft.com/office/word/2010/wordml" xmlns:w15="http://schemas.microsoft.com/office/word/2012/wordml" xmlns:mc="http://schemas.openxmlformats.org/markup-compatibility/2006" mc:Ignorable="w14 w15">
  <w:body>
    <!-- Header Bar: Title and Status -->
    <w:tbl>
      <w:tblPr>
        <w:tblW w:w="5000" w:type="pct"/>
        <w:tblBorders><w:bottom w:val="single" w:sz="12" w:space="0" w:color="CCCCCC"/></w:tblBorders>
        <w:tblCellMar><w:bottom w:w="100" w:type="dxa"/></w:tblCellMar>
      </w:tblPr>
      <w:tblGrid><w:gridCol w:w="7000"/><w:gridCol w:w="3450"/></w:tblGrid>
      <w:tr w:rsidR="00000000">
        <w:tc>
          <w:tcPr><w:tcW w:w="3350" w:type="pct"/><w:vAlign w:val="center"/></w:tcPr>
          <w:p><w:pPr><w:spacing w:after="0"/></w:pPr><w:r><w:rPr><w:rFonts w:ascii="Arial" w:hAnsi="Arial"/><w:b/><w:sz w:val="30"/><w:szCs w:val="30"/><w:color w:val="111827"/></w:rPr><w:t>Preview MPR - ${escapeXml(mprNo)}</w:t></w:r></w:p>
        </w:tc>
        <w:tc>
          <w:tcPr><w:tcW w:w="1650" w:type="pct"/><w:vAlign w:val="center"/></w:tcPr>
          <w:p><w:pPr><w:jc w:val="right"/><w:spacing w:after="0"/></w:pPr><w:r><w:rPr><w:rFonts w:ascii="Arial" w:hAnsi="Arial"/><w:b/><w:color w:val="111827"/><w:sz w:val="25"/><w:szCs w:val="25"/></w:rPr><w:t>Status: ${escapeXml(status)}</w:t></w:r></w:p>
        </w:tc>
      </w:tr>
    </w:tbl>

    <w:p><w:pPr><w:spacing w:before="40" w:after="40"/></w:pPr></w:p>

    <!-- 2-Column Form Fields Grid -->
    <!-- MPR Fields Grid -->
    <w:tbl>
      ${tableProps}

      <w:tblGrid>
        <w:gridCol w:w="5225"/>
        <w:gridCol w:w="5225"/>
      </w:tblGrid>

      ${fieldRowsXml.join("")}
    </w:tbl>

    <!-- Replacement Details Table (if any) -->
    ${replacementSectionXml}

    <!-- Job Description and Business Requirement Table -->
    <w:tbl>
      ${tableProps}
      <w:tblGrid><w:gridCol w:w="5225"/><w:gridCol w:w="5225"/></w:tblGrid>
      <w:tr w:rsidR="00000000">
        <w:trPr><w:tblCellSpacing w:w="80" w:type="dxa"/></w:trPr>
        ${makeBoxCell("Job Description", jdText, 2500)}
        ${makeBoxCell("Business Requirement", businessRequirement, 2500)}
      </w:tr>
    </w:tbl>

    <!-- Additional Remarks Table -->
    <w:tbl>
      ${tableProps}
      <w:tblGrid><w:gridCol w:w="10450"/></w:tblGrid>
      <w:tr w:rsidR="00000000">
        <w:trPr><w:tblCellSpacing w:w="80" w:type="dxa"/></w:trPr>
        ${makeBoxCell("Additional Remarks, if any", additionalRemarks, 5000)}
      </w:tr>
    </w:tbl>

    <w:sectPr>
      <w:pgSz w:w="11906" w:h="16838"/>
      <w:pgMar w:top="540" w:right="600" w:bottom="540" w:left="600" w:header="708" w:footer="708" w:gutter="0"/>
      <w:cols w:space="720"/>
      <w:docGrid w:linePitch="360"/>
    </w:sectPr>
  </w:body>
</w:document>`;

  const stylesXml = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<w:styles xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">
  <w:docDefaults>
    <w:rPrDefault>
      <w:rPr>
        <w:rFonts w:ascii="Arial" w:hAnsi="Arial" w:cs="Arial"/>
        <w:color w:val="111827"/>
        <w:sz w:val="25"/>
        <w:szCs w:val="25"/>
      </w:rPr>
    </w:rPrDefault>
    <w:pPrDefault/>
  </w:docDefaults>
  <w:style w:type="paragraph" w:default="1" w:styleId="Normal">
    <w:name w:val="Normal"/>
    <w:qFormat/>
    <w:rPr>
      <w:rFonts w:ascii="Arial" w:hAnsi="Arial" w:cs="Arial"/>
      <w:sz w:val="25"/>
      <w:szCs w:val="25"/>
      <w:color w:val="111827"/>
    </w:rPr>
  </w:style>
  <w:style w:type="character" w:default="1" w:styleId="DefaultParagraphFont">
    <w:name w:val="Default Paragraph Font"/>
    <w:uiPriority w:val="1"/>
    <w:semiHidden/>
    <w:unhideWhenUsed/>
  </w:style>
  <w:style w:type="table" w:default="1" w:styleId="TableNormal">
    <w:name w:val="Normal Table"/>
    <w:uiPriority w:val="99"/>
    <w:semiHidden/>
    <w:unhideWhenUsed/>
    <w:tblPr>
      <w:tblInd w:w="0" w:type="dxa"/>
      <w:tblCellMar>
        <w:top w:w="0" w:type="dxa"/>
        <w:left w:w="108" w:type="dxa"/>
        <w:bottom w:w="0" w:type="dxa"/>
        <w:right w:w="108" w:type="dxa"/>
      </w:tblCellMar>
    </w:tblPr>
  </w:style>
</w:styles>`;

  const settingsXml = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<w:settings xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main" xmlns:m="http://schemas.openxmlformats.org/officeDocument/2006/math" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">
  <w:defaultTabStop w:val="720"/>
  <w:characterSpacingControl w:val="doNotCompress"/>
  <w:compat>
    <w:compatSetting w:name="compatibilityMode" w:uri="http://schemas.microsoft.com/office/word" w:val="15"/>
  </w:compat>
</w:settings>`;

  const fontTableXml = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<w:fonts xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">
  <w:font w:name="Arial">
    <w:panose1 w:val="020B0604020202020204"/>
    <w:charset w:val="00"/>
    <w:family w:val="swiss"/>
    <w:pitch w:val="variable"/>
  </w:font>
</w:fonts>`;

  const zip = new JSZip();

  zip.file(
    "[Content_Types].xml",
    `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">
  <Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>
  <Default Extension="xml" ContentType="application/xml"/>
  <Override PartName="/word/document.xml" ContentType="application/vnd.openxmlformats-officedocument.wordprocessingml.document.main+xml"/>
  <Override PartName="/word/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.wordprocessingml.styles+xml"/>
  <Override PartName="/word/settings.xml" ContentType="application/vnd.openxmlformats-officedocument.wordprocessingml.settings+xml"/>
  <Override PartName="/word/fontTable.xml" ContentType="application/vnd.openxmlformats-officedocument.wordprocessingml.fontTable+xml"/>
</Types>`
  );

  zip.file(
    "_rels/.rels",
    `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="word/document.xml"/>
</Relationships>`
  );

  zip.file(
    "word/_rels/document.xml.rels",
    `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/>
  <Relationship Id="rId2" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/settings" Target="settings.xml"/>
  <Relationship Id="rId3" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/fontTable" Target="fontTable.xml"/>
</Relationships>`
  );

  zip.file("word/styles.xml", stylesXml);
  zip.file("word/settings.xml", settingsXml);
  zip.file("word/fontTable.xml", fontTableXml);
  zip.file("word/document.xml", documentXml);

  const safeMpr = String(mprNo).replace(/[^a-zA-Z0-9_-]/g, "_");
  const fileName = `Preview_MPR_${safeMpr}.docx`;

  return { zip, fileName, mprNo };
};

export const generateMprDocAttachment = async (record, customFileName) => {
  if (!record) return null;
  try {
    const { zip, fileName } = buildMprDocZip(record);
    const base64 = await zip.generateAsync({
      type: "base64",
      compression: "DEFLATE",
    });

    return {
      filename: customFileName || fileName,
      content: base64,
      encoding: "base64",
      contentType: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    };
  } catch (error) {
    console.error("generateMprDocAttachment error:", error);
    return null;
  }
};

export const downloadMprAsDoc = async (record) => {
  if (!record) {
    toast.error("No MPR data available to download");
    return;
  }

  try {
    const { zip, fileName } = buildMprDocZip(record);
    const docxBlob = await zip.generateAsync({
      type: "blob",
      mimeType: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      compression: "DEFLATE",
    });

    const url = URL.createObjectURL(docxBlob);
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    toast.success("MPR document downloaded successfully");
  } catch (error) {
    console.error("Error downloading MPR docx:", error);
    toast.error("Failed to download MPR document");
  }
};
